import { getRequest, postRequest, putRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import { AlertTargetForwardType, CurrentUserV5, Error, SortOrder, User } from 'lib/types'
import { API_USER, API_USERS, AUTH_PROVIDER, AuthProvider, API_RESEND_INVITE, API_V5_USERS } from 'lib/constants'
import { decodeJsonApiObject } from 'models/api'
import { PopUpNotifications } from 'models'
import { trans, TranslationGroup, TranslationKey } from 'lib/types'

// v5 pages at 50 rows and ignores a `limit` query param.
export const USERS_PAGE_SIZE = 50

export interface UsersPageParams {
  page?: number
  orgId?: string | null
  search?: string
  sortBy?: { field: string; order: SortOrder }
  filterBy?: { field: string; value: string | number | boolean }
}

export interface UsersPageResult {
  users: User[]
  currentPage: number
  totalCount: number
  totalPages: number
}

export function getUserById(id: string): Promise<User> {
  return getRequest({
    endpoint: `${API_USERS}/${id}`,
  }).then(({ data }) => {
    return { ...data.data, lastFetched: new Date().getTime() }
  })
}

// TODO: switch to using decodeJsonApiObject!!!
export const getCurrentUser = (): Promise<User> =>
  getRequest({
    endpoint: `${API_USER}/current`,
    handleSuspendedOrg: true, // For forbidden - suspended account redirect flag
  }).then(({ data }) => {
    return { ...data.data, lastFetched: new Date().getTime() }
  })

  export const getCurrentUserV5 = (): Promise<CurrentUserV5> =>
  getRequest({
    endpoint: `${API_V5_USERS}/current`,
  }).then(({ data }) => {
    return { ...data.data, lastFetched: new Date().getTime() }
  })
  
// Fetch a single page of users from v5. Same query-param contract as the other v5
// endpoints (search, sort, filter, pagination, optional org scoping via `org_id` header).
// Response shape: { data: [ {id, email, firstName, ...} ], meta: { currentPage, totalCount } }
// The app still speaks the v4 { id, type, attributes } shape, so we adapt here.
// NOTE: v5 does not preload `grants` on users; UsersPage repopulates attributes.grants
// from the Grants store client-side so CSV export keeps working.
export async function getUsersPage(params: UsersPageParams = {}): Promise<UsersPageResult> {
  const page = params.page ?? 1
  const scoped = params.orgId && params.orgId !== ALL_ORGS_SELECTED
  const qs: string[] = [`page=${page}`]
  if (params.search) qs.push(`search=${encodeURIComponent(params.search)}`)
  if (params.sortBy) {
    const prefix = params.sortBy.order === SortOrder.Descending ? '-' : ''
    qs.push(`sort=${prefix}${params.sortBy.field}`)
  }
  if (params.filterBy) {
    qs.push(`filter[${params.filterBy.field}]=${encodeURIComponent(String(params.filterBy.value))}`)
  }
  const res: any = await getRequest({
    endpoint: `${API_V5_USERS}?${qs.join('&')}`,
    header: scoped ? { org_id: params.orgId as string } : {},
  })
  const items: any[] = res?.data?.data || []
  const meta = res?.data?.meta || {}
  // Server returns currentPage=0 when totalCount=0 (org has no users yet). Storing that
  // and feeding it back as ?page=0 on the next refresh causes Postgres negative-offset
  // errors → 500. Clamp to >= 1 so we always round-trip a valid page number.
  const currentPage: number = Math.max(1, meta.currentPage ?? page)
  const totalCount: number = meta.totalCount ?? items.length
  const totalPages = Math.max(1, Math.ceil(totalCount / USERS_PAGE_SIZE))
  const users = items.map(({ id, ...rest }) => ({ id, type: 'user', attributes: rest })) as User[]
  return { users, currentPage, totalCount, totalPages }
}

export function getUsersByOrgId(orgId: string): Promise<User[]> {
  return getRequest({
    endpoint: `${API_USERS}`,
    header: {
      org_id: orgId,
    },
  }).then(({ data }) => data.data)
}

export function getUserByEmail(email: string): Promise<{ provider: AuthProvider | null }> {
  const translation: TranslationGroup = trans.group(TranslationKey.AUTH)
  return getRequest({
    endpoint: `${AUTH_PROVIDER}${email}`,
  })
    .then(({ data }) => data)
    .catch(() => PopUpNotifications.fireError({ content: translation.unexpected_error }))
}

export function createOrUpdateUser(userWithId: User): Promise<User> {
  const { id, ...user } = userWithId

  return (
    id
      ? putRequest({
          endpoint: `${API_USERS}/${id}`,
          data: { user },
        })
      : postRequest({
          endpoint: `${API_USERS}`,
          data: { user },
        })
  ).then(({ data }) => decodeJsonApiObject<User>(data.data))
}

export interface ProvisionAlertTarget {
  forward_type: AlertTargetForwardType
  forward: string
}

export interface ProvisionUserParams {
  email: string
  grants: string[]
  alert_targets: ProvisionAlertTarget[]
  voice_license: boolean
}

export interface ProvisionedUser {
  id: string
  email: string
  // Present when provisioning matched a user who already existed in another org.
  // The endpoint identifies a user by email alone and never writes a name, so
  // these report what was already on file rather than anything we just sent.
  firstName?: string | null
  lastName?: string | null
  grants: { id: string; name: string; orgId: string }[]
  alertTargets: { id: string; forward: string; forwardType: string; status: string }[]
  licenses: { id: string; orgId: string; type: string }[]
}

// One transaction for the whole org membership — user, grants, alert-target
// subscriptions, responder, optional voice seat. A refusal anywhere rolls all of
// it back, so a rejection can't leave a half-provisioned user behind.
//
// Body keys are snake_case to match the endpoint's schema; the target `status` is
// deliberately not sendable, the server derives it from `forward_type`.
export function provisionUser(orgId: string, params: ProvisionUserParams): Promise<ProvisionedUser> {
  return postRequest({
    endpoint: API_V5_USERS,
    data: params,
    header: { org_id: orgId },
  }).then(({ data }: any) => data?.data ?? data)
}

export function resendInviteEmail(email: string): Promise<any> {
  return postRequest({
    endpoint: API_RESEND_INVITE,
    data: { user: { email: email } },
  })
}

export const getV5UsersCSV = (orgId: string): Promise<void> => {
  return getRequest({
    endpoint: `${API_V5_USERS}/download`,
    header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
  })
    .then((response: any) => {
      const csvData: string = response.data

      const blob = new Blob([csvData], { type: 'text/csv' })

      const link = document.createElement('a')
      link.download = `users.csv`
      link.href = window.URL.createObjectURL(blob)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })
    .catch((error: Error) => {
      console.error('Error fetching CSV data:', error)
      throw error
    })
}
